import React from 'react';
import _ from 'lodash';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { styled } from '@mui/material/styles';
import { Box, LinearProgress } from '@mui/material';
import { useMutation } from 'react-fetching-library';

import ContextMenu from './context-menu/ContextMenu';
import ContextMenuModal from './context-menu/ContextMenuModal';


import { plPL } from '@mui/x-data-grid';
import { DataGridToolbar } from '../../../components-core/data-grid/DataGridToolbar';
import { DataGridPro, GRID_CHECKBOX_SELECTION_COL_DEF } from '@mui/x-data-grid-pro';

import OrderDetails from './OrderDetails';
import { orderColumns } from '../../../services/column-definitions';
import { setOrderHiddenColumns, setOrder, setAllowAutoOrderUpdate, setOrderColumnsWidth, setOrderColumnsSequence } from '../../../services/redux-actions';
import { updateEmployeeConfigAction } from '../../../services/request';
import { useResponsive } from '../../../services/hooks';
// ----------------------------------------------------------------------

const RootStyle = styled('div')({
  flexGrow: 1,
  display: 'flex',
  overflow: 'hidden',
  flexDirection: 'column'
});

// ----------------------------------------------------------------------

OrderList.propTypes = {
  onOpenSidebar: PropTypes.func
};

export default function OrderList({ onOpenSidebar, hideSidebar, count, queryCallback, loading, orders, updateOrders, removeOrderFromOrders, addOrder, refreshOrders }) {
  const dispatch = useDispatch();
  const { mutate } = useMutation(updateEmployeeConfigAction);
  const { employeeId, employeeConfigId } = useSelector(state => state.app);
  const { orderHiddenColumns, orderColumnsWidth, orderColumnsSequence } = useSelector(state => state.employeeOrderConfig);
  const [openCompose, setOpenCompose] = React.useState(false);
  const [fullScreen, setFullScreen] = React.useState(false);
  const [hiddenColumns, setHiddenColumns] = React.useState(orderHiddenColumns || []);
  const [rowsState, setRowsState] = React.useState({
    page: 0,
    pageSize: 100,
    rows: orders,
    loading: false,
    sortingFields: []
  });
  const [update, setUpdate] = React.useState(true);

  const [selectedOrder, setSelectedOrder] = React.useState({});
  const [contextMenu, setContextMenu] = React.useState(null);
  const [openState, setOpenState] = React.useState(false);
  const [modalType, setModalType] = React.useState('');
  const [openModal, setOpenModal] = React.useState(false);
  const isDesktop = useResponsive('up', 'lg');

  const handleOpenModal = type => {
    setModalType(type);
    setOpenModal(true);
    handleClose();
    dispatch(setAllowAutoOrderUpdate(false));
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    dispatch(setAllowAutoOrderUpdate(true));
    if (modalType === 'changeState') {
      setModalType('');
    }
    refreshOrders();
  };

  const handleContextMenu = event => {
    event.preventDefault();
    const orderId = Number(event.currentTarget.getAttribute('data-id'));
    const order = orders.find(order => order.id === orderId);
    console.log(order);
    setSelectedOrder(order);
    setContextMenu(
      contextMenu === null
        ? { mouseX: event.clientX - 2, mouseY: event.clientY - 4 }
        : null
    );
    dispatch(setAllowAutoOrderUpdate(false));
  };

  const handleClose = () => {
    setContextMenu(null);
    setOpenState(false);
    dispatch(setAllowAutoOrderUpdate(true));
  };

  const handleStateClick = () => {
    setOpenState(!openState);
  };

  const handleDetailsCheck = (data, doubleClick = false) => {
    if (doubleClick) {
      setFullScreen(true);
    }
    setOpenCompose(true);
    dispatch(setOrder(data));
    dispatch(setAllowAutoOrderUpdate(false));
  };
  const [columns, setColumns] = React.useState(orderColumns(handleDetailsCheck));

  React.useEffect(() => {
    if (update) {
      if (_.isNil(orderHiddenColumns)) {
        setHiddenColumns([]);
      } else {
        const columnsClone = _.cloneDeep(columns);
        const idxs = [];
        _.forEach(orderHiddenColumns, item => {
          const idx = columnsClone.findIndex((obj => obj.field == item));
          if (idx > -1) {
            idxs.push(idx);
            columnsClone[idx].hide = true;
          }
        });
        _.map(columnsClone, (obj, idx) => {
          if (!idxs.includes(idx)) {
            columnsClone[idx].hide = false;
          }
          if (obj.field in orderColumnsWidth) {
            columnsClone[idx].width = orderColumnsWidth[obj.field];
          }
        });
        _.map(orderColumnsSequence, (item, itemIdx) => {
          const idx = _.findIndex(columnsClone, obj => obj.field == item);
          if (idx != itemIdx + 1) {
            [columnsClone[idx], columnsClone[itemIdx + 1]] = [columnsClone[itemIdx + 1], columnsClone[idx]];

          }
        });
        setColumns(columnsClone);
        setHiddenColumns(orderHiddenColumns);
      }
    } else {
      setUpdate(true);
    }
  }, [orderHiddenColumns, orderColumnsWidth, orderColumnsSequence]);

  React.useEffect(() => {
    setRowsState({ ...rowsState, rows: orders });
  }, [orders]);

  React.useEffect(() => {
    queryCallback(rowsState.page, rowsState.pageSize, null, rowsState.sortingFields);
    setRowsState(prev => ({ ...prev, loading: false }));
  }, [rowsState.page, rowsState.pageSize, rowsState.sortingFields]);

  const handleColumnVisibilityChange = async col => {
    const columnsClone = _.cloneDeep(columns);
    const idx = _.findIndex(columns, obj => obj.field == col.field);
    columnsClone[idx].hide = !columnsClone[idx].hide;
    setColumns(columnsClone);
    const hiddenColumnsClone = [...hiddenColumns];
    const hiddenIdx = hiddenColumnsClone.indexOf(col.field);
    setUpdate(false);
    if (hiddenIdx > -1) {
      hiddenColumnsClone.splice(hiddenIdx, 1);
      setHiddenColumns(hiddenColumnsClone);
      const { payload } = dispatch(setOrderHiddenColumns(hiddenColumnsClone));
      await mutate({ order_hidden_columns: payload, employee: employeeId }, employeeConfigId);
    } else {
      setHiddenColumns([col.field, ...hiddenColumns]);
      const { payload } = dispatch(setOrderHiddenColumns([col.field, ...hiddenColumns]));
      await mutate({ order_hidden_columns: payload, employee: employeeId }, employeeConfigId);
    }
  };

  const searchCallback = search => {
    queryCallback(rowsState.page, rowsState.pageSize, search, rowsState.sortingFields);
  };

  const handleColumnResizeChange = async colDef => {
    const { payload } = dispatch(setOrderColumnsWidth({ [colDef.field]: colDef.width }));
    await mutate({ order_columns_width: payload, employee: employeeId }, employeeConfigId);
  };

  const handleColumnsOrderChange = async e => {
    const orderColumnsSequenceClone = [...orderColumnsSequence];
    [orderColumnsSequenceClone[e.oldIndex - 2], orderColumnsSequenceClone[e.targetIndex - 2]] = [orderColumnsSequenceClone[e.targetIndex - 2], orderColumnsSequenceClone[e.oldIndex - 2]];
    const { payload } = dispatch(setOrderColumnsSequence(orderColumnsSequenceClone));
    await mutate({ order_columns_sequence: payload, employee: employeeId }, employeeConfigId);
  };

  const handleSortModelChange = sortModel => {
    let sorrtingArr = [];

    for (const item of sortModel) {
      if (item.sort == 'asc') {
        sorrtingArr.push(item.field);
      } else if (item.sort == 'desc') {
        sorrtingArr.push(`-${item.field}`);
      }
    }

    if (_.isEmpty(sorrtingArr)) {
      sorrtingArr = ['-id'];
    }

    console.log('sorrtingArr: ', sorrtingArr);

    setRowsState(prev => ({ ...prev, sortingFields: sorrtingArr }));
  };

  return (
    <RootStyle>
      <OrderDetails
        updateOrders={updateOrders}
        isOpenCompose={openCompose}
        parentFullScreen={fullScreen}
        refreshOrders={refreshOrders}
        onCloseCompose={() => {
          setOpenCompose(false);
          setFullScreen(false);
        }} />
      <Box sx={isDesktop ? { minWidth: { md: 800 }, height: '100%' } : { width: '100%', height: '80vh' }}>
        <DataGridPro
          onColumnResize={e => handleColumnResizeChange(e.colDef)}
          // onColumnOrderChange={e => handleColumnsOrderChange(e)}
          columns={columns}
          rows={orders}
          density="compact"
          disableColumnMenu={true}
          autoPageSize={false}
          rowsPerPageOptions={[10, 25, 50, 100, 250]}
          components={{
            Toolbar: DataGridToolbar,
            LoadingOverlay: () => <LinearProgress sx={{ zIndex: 2 }} />
          }}
          componentsProps={{
            toolbar: { onOpenSidebar, hideSidebar, searchCallback, showAll: true, advancedFilter: true },
            row: {
              onContextMenu: handleContextMenu,
              style: { cursor: 'context-menu' }
            }
          }}
          initialState={{ pinnedColumns: { left: [GRID_CHECKBOX_SELECTION_COL_DEF.field, 'Szczegóły'] } }}
          localeText={plPL.components.MuiDataGrid.defaultProps.localeText}
          disableSelectionOnClick
          onRowClick={params => handleDetailsCheck(params.row)}
          onRowDoubleClick={params => handleDetailsCheck(params.row, true)}
          onColumnVisibilityChange={col => handleColumnVisibilityChange(col)}
          pagination
          rowCount={count}
          {...rowsState}
          loading={loading}
          paginationMode="server"
          onPageChange={page => setRowsState(prev => ({ ...prev, page }))
          }
          onPageSizeChange={pageSize => setRowsState(prev => ({ ...prev, pageSize }))
          }
          sortingMode="server"
          onSortModelChange={handleSortModelChange}
        />
        <ContextMenu contextMenu={contextMenu} handleClose={handleClose} handleOpenModal={handleOpenModal} />
      </Box>
      <ContextMenuModal type={modalType} order={selectedOrder} open={openModal} handleClose={handleCloseModal} removeOrderFromOrders={removeOrderFromOrders} addOrder={addOrder} />
    </RootStyle>
  );
}
